// Casting a spell (issue 1530; docs/MRCS/shadowrun-3.md §9 step 16; the book p. 178-183, 191): where the table's system
// declares spellcasting (`features.combat.spellcasting`), a caster casts a known spell at a Force no higher than the one
// it was learned at. Every step is the system's data:
//
// 1. The casting test: the skill (Sorcery, or its Spellcasting specialization) and pool dice, at most the skill (the
//    pool's `spell` cap), against the target's attribute the spell names (its `target_attribute`, read from the target's
//    sheet by the system's `targets` list), or the spell's own number, or a number typed — with the TN modifiers asked
//    (sustained spells, cover, the caster's wounds, which the server joins). No successes, the spell fails; every die a
//    1, a disaster, adds the system's `disaster` to the drain's TN.
// 2. The resistance: a living target that is not willing, on a spell whose target attribute `resists`, throws that
//    attribute against the Force, with no modifier and no pool. Its successes come off the casting's; at or below none,
//    the spell does nothing.
// 3. The effect: the net successes. A damaging spell (a `damage` kind) stages the level chosen at the cast one step for
//    every `stage_per` net successes, at most the top level; its code is the Force and that level ("4M Stun"). A spell
//    resolved as a ranged attack (an elemental manipulation) is cast and drained here, and its dodge and damage left to
//    the DM in the first version.
// 4. The drain: the drain rating (Willpower) and the pool dice set aside for it against the Force (less a limited
//    spell's reduction) divided by `divide`, rounded down, plus the spell's drain modifier, plus `beyond_top` Power for
//    each level its level would pass the top, plus a disaster's. Its level is the spell's (or the level chosen, plus the
//    spell's level modifier, "Damage Level +1"); every `stage_per` successes take it down a step, below the lowest none.
//    Stun within the caster's Magic, Physical over it (the system's `kinds`).
//
// The record is an attack's (`AttackFacts.spell`, rules/attack.ts), so privacy, the log, the damage setting and the
// projection are the attack's own; the drain lands on the caster as the server commits the cast. Nothing here throws a
// die on its own: the server hands the generator in, a test a scripted list.
import { MAX_POOL, poolDisaster, poolFaces, poolText, testDice } from '../dice/pool.js';
import { codesData, parseCodes, parseTestDice } from './system.js';
import { testDiceData } from './test.js';
import { codeText, parseThrow, stagesFor, successesOf, throwLine, throwOne } from './staged.js';
import { diceSum, diceTermLine, parseDiceTerms } from './magicDice.js';
export const CAST_LIMITS = { name: 80, force: 30, rating: MAX_POOL, modifiers: 8, modifier: 50, label: 40, drainLess: 3, magic: 30, target: 100 };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const ADDRESS_RE = /^[a-z][a-z0-9_]{0,62}\/[a-z0-9][a-z0-9-]{0,127}$/;
// eslint-disable-next-line no-control-regex
const LINE_BAD = /[\u0000-\u001f\u007f]/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isText = (v, max) => typeof v === 'string' && v.trim().length >= 1 && v.length <= max && !LINE_BAD.test(v);
const only = (v, keys) => Object.keys(v).every((k) => keys.includes(k));
const isKey = (v) => typeof v === 'string' && KEY_RE.test(v);
function parseTerms(v) {
    if (v === undefined)
        return [];
    if (!Array.isArray(v) || v.length > CAST_LIMITS.modifiers)
        return null;
    const out = [];
    for (const x of v) {
        if (!isObj(x) || !only(x, ['label', 'value']) || !isText(x.label, CAST_LIMITS.label) || !isInt(x.value, -CAST_LIMITS.modifier, CAST_LIMITS.modifier))
            return null;
        out.push({ label: x.label.trim(), value: x.value });
    }
    return out;
}
/** A spell off the wire or a record: exact shape or null. */
export function parseCastSpell(v) {
    if (!isObj(v) || !only(v, ['name', 'ref', 'learned', 'target', 'drain', 'damage', 'ranged', 'category']))
        return null;
    if (v.category !== undefined && !isText(v.category, CAST_LIMITS.label))
        return null;
    if (!isText(v.name, CAST_LIMITS.name) || (v.ref !== undefined && (typeof v.ref !== 'string' || !ADDRESS_RE.test(v.ref))) || !isInt(v.learned, 1, CAST_LIMITS.force))
        return null;
    let target = null;
    if (v.target !== null) {
        const t = v.target;
        if (isObj(t) && only(t, ['attribute']) && isKey(t.attribute))
            target = { attribute: t.attribute };
        else if (isObj(t) && only(t, ['number']) && isInt(t.number, 1, CAST_LIMITS.target))
            target = { number: t.number };
        else
            return null;
    }
    const d = v.drain;
    if (!isObj(d) || !only(d, ['modifier', 'level', 'levelModifier']) || !isInt(d.modifier, -10, 10) || (d.level !== undefined && !isKey(d.level)) || !isInt(d.levelModifier, -4, 4))
        return null;
    if ((v.damage !== undefined && !isKey(v.damage)) || (v.ranged !== undefined && v.ranged !== true))
        return null;
    return {
        name: v.name.trim(), ...(typeof v.ref === 'string' ? { ref: v.ref } : {}), learned: v.learned, target,
        drain: { modifier: d.modifier, ...(typeof d.level === 'string' ? { level: d.level } : {}), levelModifier: d.levelModifier },
        ...(typeof v.damage === 'string' ? { damage: v.damage } : {}), ...(v.ranged === true ? { ranged: true } : {}),
        ...(typeof v.category === 'string' ? { category: v.category.trim() } : {}),
    };
}
/** A cast request from the wire: exact shape or null. Untrusted input, always. */
export function parseCastRequest(v) {
    if (!isObj(v) || v.kind !== 'spell.cast')
        return null;
    if (!only(v, ['kind', 'caster', 'target', 'spell', 'force', 'level', 'skill', 'tn', 'rating', 'modifiers', 'dice', 'resistDice', 'innate', 'drainModifiers', 'pool', 'drainLess', 'willing', 'magic', 'drainRating', 'private']))
        return null;
    if (!UUID_RE.test(String(v.caster)) || !UUID_RE.test(String(v.target)))
        return null;
    const spell = parseCastSpell(v.spell);
    if (!spell || !isInt(v.force, 1, spell.learned) || (v.level !== undefined && !isKey(v.level)))
        return null;
    const s = v.skill;
    if (!isObj(s) || !only(s, ['name', 'rating', 'ref']) || !isText(s.name, CAST_LIMITS.name) || !isInt(s.rating, 1, CAST_LIMITS.rating) || (s.ref !== undefined && (typeof s.ref !== 'string' || !ADDRESS_RE.test(s.ref))))
        return null;
    if ((v.tn !== undefined && !isInt(v.tn, 1, CAST_LIMITS.target)) || (v.rating !== undefined && !isInt(v.rating, 1, CAST_LIMITS.rating)))
        return null;
    const modifiers = parseTerms(v.modifiers);
    const drainModifiers = parseTerms(v.drainModifiers);
    const dice = parseDiceTerms(v.dice);
    const resistDice = parseDiceTerms(v.resistDice);
    if (!modifiers || !drainModifiers || !dice || !resistDice || (v.innate !== undefined && v.innate !== true))
        return null;
    let pool;
    if (v.pool !== undefined) {
        const p = v.pool;
        if (!isObj(p) || !only(p, ['key', 'cast', 'drain']) || !isKey(p.key) || !isInt(p.cast, 0, CAST_LIMITS.rating) || !isInt(p.drain, 0, CAST_LIMITS.rating) || p.cast + p.drain < 1)
            return null;
        pool = { key: p.key, cast: p.cast, drain: p.drain };
    }
    if ((v.drainLess !== undefined && !isInt(v.drainLess, 0, CAST_LIMITS.drainLess)) || (v.willing !== undefined && v.willing !== true))
        return null;
    if ((v.magic !== undefined && !isInt(v.magic, 0, CAST_LIMITS.magic)) || (v.drainRating !== undefined && !isInt(v.drainRating, 1, CAST_LIMITS.rating)))
        return null;
    if (typeof v.private !== 'boolean' && v.private !== undefined)
        return null;
    return {
        kind: 'spell.cast', caster: String(v.caster), target: String(v.target), spell, force: v.force, ...(typeof v.level === 'string' ? { level: v.level } : {}),
        skill: { name: s.name.trim(), rating: s.rating, ...(typeof s.ref === 'string' ? { ref: s.ref } : {}) },
        ...(typeof v.tn === 'number' ? { tn: v.tn } : {}), ...(typeof v.rating === 'number' ? { rating: v.rating } : {}),
        modifiers, ...(dice.length ? { dice } : {}), ...(v.resistDice !== undefined ? { resistDice } : {}), ...(v.innate === true ? { innate: true } : {}), drainModifiers, ...(pool ? { pool } : {}), ...(typeof v.drainLess === 'number' && v.drainLess ? { drainLess: v.drainLess } : {}),
        ...(v.willing === true ? { willing: true } : {}), ...(typeof v.magic === 'number' ? { magic: v.magic } : {}), ...(typeof v.drainRating === 'number' ? { drainRating: v.drainRating } : {}),
        private: v.private === true,
    };
}
export const NO_CAST_RULES = "This table's game system names no spellcasting, so the table cannot cast a spell.";
/** The word the disaster's addition to the drain's TN is shown with. */
export const CAST_DISASTER = 'disaster';
/**
 * Where the drain's level lands before the resistance (p. 191): the spell's own level (by index), or the level chosen
 * plus the spell's level modifier ("Damage Level +1"); past the top it stays at the top and adds `beyondTop` Power for
 * each level over, and below the lowest it is the lowest. Null when the level depends on one not chosen.
 */
export function drainStart(own, chosen, levelModifier, top, beyondTop) {
    if (own === null && chosen < 0)
        return null;
    const want = own !== null ? own : chosen + levelModifier;
    return { at: Math.max(0, Math.min(top, want)), extra: want > top ? (want - top) * beyondTop : 0 };
}
/** The word a wound's TN modifier is shown with. */
const WOUNDED = 'wounded';
/**
 * A cast made concrete (§9 step 16): the casting test, the resistance when the target makes one, and the drain test,
 * each thrown with the generator by the system's rules. A string is a refusal in words.
 */
export function makeCast(req, m, rng) {
    const r = m.rules;
    const levels = r.codes.levels;
    const top = levels.length - 1;
    const chosen = req.level === undefined ? -1 : levels.findIndex((l) => l.key === req.level);
    if (req.level !== undefined && chosen < 0)
        return 'That damage level is not one of this game system\'s.';
    const damaging = req.spell.damage !== undefined;
    if ((damaging || req.spell.drain.level === undefined) && chosen < 0)
        return 'Choose the damage level the spell is cast at.';
    if (req.spell.drain.level !== undefined && !levels.some((l) => l.key === req.spell.drain.level))
        return 'That drain level is not one of this game system\'s.';
    const kind = damaging ? m.kinds.find((k) => k.key === req.spell.damage) : null;
    if (damaging && !kind)
        return 'That kind of damage is not one of this game system\'s.';
    // The casting's TN: the target's attribute (as read), the spell's own number, or the number typed.
    let target;
    let base;
    const attr = req.spell.target && 'attribute' in req.spell.target ? r.targets.find((t) => t.key === req.spell.target.attribute) : null;
    if (req.spell.target && 'attribute' in req.spell.target && !attr)
        return 'That target attribute is not one of this game system\'s.';
    if (req.tn !== undefined) {
        target = req.tn;
        base = '';
    }
    else if (attr) {
        if (m.targetRating === null)
            return `The target's ${attr.label} could not be read from its sheet: type the target number.`;
        target = m.targetRating;
        base = attr.label;
    }
    else if (req.spell.target && 'number' in req.spell.target) {
        target = req.spell.target.number;
        base = '';
    }
    else
        return 'This spell names no target number: type it.';
    if (m.magic === null)
        return `The caster's ${r.magic.label} could not be read from its sheet: type it.`;
    if (m.drainRating === null)
        return `The caster's ${r.drain.rating.label} could not be read from its sheet: type it.`;
    if (req.innate && req.pool?.drain)
        return 'An innate power is cast without drain: set no pool dice aside for it.';
    const spent = req.pool && m.poolLabel ? { key: req.pool.key, label: m.poolLabel } : null;
    const terms = [...req.modifiers, ...(m.wound ? [{ label: WOUNDED, value: Math.max(-CAST_LIMITS.modifier, Math.min(CAST_LIMITS.modifier, m.wound)) }] : [])];
    const cast = throwOne(r.dice, {
        label: req.skill.name, rating: req.skill.rating, spent: spent && req.pool.cast ? { ...spent, dice: req.pool.cast } : null,
        // The sheet's own dice (issue 1172) add to the dice rolled, never the skill, so the pool's cap stays the skill's.
        dice: Math.max(0, req.skill.rating + (spent ? req.pool.cast : 0) + diceSum(req.dice)), ...(req.dice?.length ? { bonus: req.dice.map((x) => ({ ...x })) } : {}), target, base, terms,
    }, rng);
    if (typeof cast === 'string')
        return cast;
    const rules = testDiceData(r.dice);
    const castDice = parseTestDice(rules);
    const castConcrete = castDice ? testDice(castDice, { dice: cast.dice, target: cast.tn }) : null;
    const disaster = castConcrete ? poolDisaster(castConcrete, cast.pool) : false;
    // The resistance (p. 183): a target that is not the caster and not willing, on an attribute that resists, when the
    // casting scored at all; its rating against the Force, no modifiers.
    let resist;
    const successes = successesOf(rules, cast);
    if (successes > 0 && attr?.resists && !req.willing && !m.self && !req.spell.ranged) {
        const rating = req.rating ?? m.targetRating;
        if (rating === null)
            return `The target's ${attr.label} could not be read from its sheet: type it.`;
        // The target's own dice (issue 1172: a power's on the resist test) add to the dice rolled, each its own line.
        const thrown = throwOne(r.dice, { label: attr.label, rating, spent: null, dice: Math.max(0, rating + diceSum(req.resistDice)), ...(req.resistDice?.length ? { bonus: req.resistDice.map((x) => ({ ...x })) } : {}), target: req.force, base: 'Force', terms: [] }, rng);
        if (typeof thrown === 'string')
            return thrown;
        resist = thrown;
    }
    // The drain (p. 183): the Force counted, halved, its modifier and any Power past the top, and a disaster's addition.
    const less = req.drainLess ?? 0;
    const force = Math.max(1, req.force - less);
    const half = Math.floor(force / r.drain.divide);
    const own = req.spell.drain.level === undefined ? null : levels.findIndex((l) => l.key === req.spell.drain.level);
    const start = drainStart(own, chosen, req.spell.drain.levelModifier, top, r.drain.beyondTop);
    if (!start)
        return 'Choose the damage level the spell is cast at.';
    const over = force > m.magic;
    const drainKind = m.kinds.find((k) => k.key === (over ? r.drain.kinds.over : r.drain.kinds.within));
    if (!drainKind)
        return 'The drain\'s kind of damage is not one of this game system\'s.';
    const drainTerms = [...req.drainModifiers, ...(disaster && r.drain.disaster ? [{ label: CAST_DISASTER, value: r.drain.disaster }] : [])];
    const drainTarget = Math.max(1, half + req.spell.drain.modifier + start.extra);
    const drainSpent = spent && req.pool.drain ? { ...spent, dice: req.pool.drain } : null;
    const drainThrow = throwOne(r.dice, {
        label: r.drain.rating.label, rating: m.drainRating, spent: drainSpent, dice: m.drainRating + (drainSpent?.dice ?? 0), target: drainTarget, base: 'Force', terms: drainTerms,
    }, rng);
    if (typeof drainThrow === 'string')
        return drainThrow;
    return {
        rules, codes: parseCodes(codesData(r.codes)), spell: req.spell, force: req.force, level: chosen >= 0 ? levels[chosen].key : null,
        damageKind: kind ? { key: kind.key, label: kind.label, ...(kind.key !== m.kinds[0]?.key ? { named: true } : {}) } : null,
        cast, ...(resist ? { resist } : {}),
        drain: { ...drainThrow, force, divide: r.drain.divide, half, modifier: req.spell.drain.modifier, extra: start.extra, level: levels[start.at].key, kind: drainKind.key, kindLabel: drainKind.label },
        magic: m.magic, drainLess: less, ...(req.innate ? { innate: true } : {}),
    };
}
const disasterOf = (rules, t) => {
    const d = parseTestDice(rules);
    const dice = d && t.dice ? testDice(d, { dice: t.dice, target: t.tn }) : null;
    return dice ? poolDisaster(dice, t.pool) : false;
};
/** The outcome as the record stands. */
export function castOutcome(f) {
    const levels = f.codes.levels;
    const cast = successesOf(f.rules, f.cast);
    const disaster = disasterOf(f.rules, f.cast);
    const resist = f.resist ? successesOf(f.rules, f.resist) : null;
    const net = cast - (resist ?? 0);
    const from = levels.findIndex((l) => l.key === f.drain.level);
    const dSucc = successesOf(f.rules, f.drain);
    const dAt = from - Math.floor(dSucc / f.codes.stagePer);
    const drain = { successes: dSucc, from: { ...levels[from] }, level: dAt >= 0 ? { ...levels[dAt] } : null, steps: dAt - from };
    const result = cast === 0 ? 'failed' : net <= 0 ? 'resisted' : 'effect';
    let level = null;
    let steps = 0;
    if (result === 'effect' && f.damageKind && !f.spell.ranged && f.level !== null) {
        const at0 = levels.findIndex((l) => l.key === f.level);
        const at = Math.min(levels.length - 1, at0 + stagesFor(net, f.codes.stagePer));
        level = { ...levels[at] };
        steps = at - at0;
    }
    return { result, cast, disaster, resist, net: result === 'failed' ? 0 : net, level, steps, drain };
}
/** The damage the spell deals its target: the staged level's boxes, of its kind. Null for none. */
export function castDamage(f) {
    const o = castOutcome(f);
    return o.level && f.damageKind ? { boxes: o.level.boxes, kind: f.damageKind.key, kindLabel: f.damageKind.label, level: o.level } : null;
}
/** The drain the caster takes: its level's boxes, of the drain's kind. Null for none. */
export function castDrain(f) {
    if (f.innate)
        return null;
    const o = castOutcome(f);
    return o.drain.level ? { boxes: o.drain.level.boxes, kind: f.drain.kind, kindLabel: f.drain.kindLabel, level: o.drain.level } : null;
}
/** The roll's own faces: the casting throw's chains. */
export const castFaces = (f) => poolFaces(f.cast.pool);
// ── Stored records ────────────────────────────────────────────────────────
/** Stored cast facts: exact shape or null. Every chain must be what its dice and TN make, the drain what its numbers make. */
export function parseCastFacts(v) {
    if (!isObj(v) || !only(v, ['rules', 'codes', 'spell', 'force', 'level', 'damageKind', 'cast', 'resist', 'drain', 'magic', 'drainLess', 'innate']) || (v.innate !== undefined && v.innate !== true))
        return null;
    const d = parseTestDice(v.rules);
    if (!d || !d.success || !isObj(v.rules))
        return null;
    const rules = testDiceData(d);
    const codes = isObj(v.codes) ? parseCodes({ levels: v.codes.levels, stage_per: v.codes.stagePer, beyond_top: v.codes.beyondTop, ...(v.codes.wielder !== undefined ? { wielder: v.codes.wielder } : {}) }) : null;
    const spell = parseCastSpell(v.spell);
    if (!codes || !spell || !isInt(v.force, 1, spell.learned) || !(v.level === null || codes.levels.some((l) => l.key === v.level)))
        return null;
    const k = v.damageKind;
    if (!(k === null || (isObj(k) && only(k, ['key', 'label', 'named']) && isKey(k.key) && isText(k.label, 24) && (k.named === undefined || k.named === true))))
        return null;
    if (Boolean(k) !== (spell.damage !== undefined) || !isInt(v.magic, 0, CAST_LIMITS.magic) || !isInt(v.drainLess, 0, CAST_LIMITS.drainLess))
        return null;
    const cast = parseThrow(v.cast, rules, d);
    const resist = v.resist === undefined ? undefined : parseThrow(v.resist, rules, d);
    if (!cast || resist === null || (resist && (resist.target !== v.force || resist.base !== 'Force' || resist.spent)))
        return null;
    const dr = v.drain;
    if (!isObj(dr))
        return null;
    const { force, divide, half, modifier, extra, level, kind, kindLabel, ...rest } = dr;
    const drain = parseThrow(rest, rules, d);
    if (!drain || !isInt(force, 1, CAST_LIMITS.force) || !isInt(divide, 1, 10) || !isInt(extra, 0, 100) || !isKey(level) || !codes.levels.some((l) => l.key === level) || !isKey(kind) || !isText(kindLabel, 24))
        return null;
    // The drain's numbers are what the cast makes them: the Force less the reduction, its half, the spell's modifier.
    if (force !== Math.max(1, v.force - v.drainLess) || half !== Math.floor(force / divide) || modifier !== spell.drain.modifier || drain.target !== Math.max(1, half + spell.drain.modifier + extra))
        return null;
    return {
        rules, codes, spell, force: v.force, level: v.level, damageKind: k ? { key: k.key, label: k.label, ...(k.named === true ? { named: true } : {}) } : null,
        cast, ...(resist ? { resist } : {}),
        drain: { ...drain, force, divide, half, modifier: modifier, extra, level, kind, kindLabel },
        magic: v.magic, drainLess: v.drainLess, ...(v.innate === true ? { innate: true } : {}),
    };
}
// ── The lines ─────────────────────────────────────────────────────────────
const plural = (n) => `${n} success${n === 1 ? '' : 'es'}`;
const signed = (n) => (n < 0 ? `− ${-n}` : `+ ${n}`);
const levelOf = (f, key) => f.codes.levels.find((l) => l.key === key);
const named = (f) => (f.damageKind?.named ? f.damageKind.label : null);
/** The casting test's line: "Cast: Spellcasting 7 + 3 Spell Pool = 10 dice vs TN 4 (Willpower): … → 4 successes". */
export const castLine = (f) => throwLine(`Cast ${f.spell.name} at Force ${f.force}`, f.rules, f.cast);
/** The resistance test's line: "Resist: Willpower 4 vs TN 4 (Force): … → 1 success". */
export const resistLine = (f) => (f.resist ? throwLine('Resist', f.rules, f.resist) : null);
/** The effect's line: "Net 4 − 1 = 3: 4M Stun staged up to 4S Stun → 6 boxes Stun", "Net 3 successes", "Resisted: no effect". */
export function effectLine(f) {
    const o = castOutcome(f);
    if (o.result === 'failed')
        return o.disaster ? 'Disaster: the spell fails' : 'No successes: the spell fails';
    const head = f.resist ? `Net ${o.cast} − ${o.resist} = ${o.cast - (o.resist ?? 0)}` : `Net ${plural(o.net)}`;
    if (o.result === 'resisted')
        return `${head}: resisted, no effect`;
    if (f.spell.ranged)
        return `${head}: thrown as a ranged attack; its dodge and damage resistance are the DM's`;
    if (!o.level || !f.damageKind || f.level === null)
        return head;
    const kind = named(f);
    const before = codeText(f.force, levelOf(f, f.level), kind);
    const after = codeText(f.force, o.level, kind);
    const moved = o.steps ? `${before} staged up to ${after}` : `${before} stays ${after}`;
    return `${head}: ${moved} → ${o.level.boxes} ${o.level.boxes === 1 ? 'box' : 'boxes'} ${f.damageKind.label}`;
}
/** The drain test's line: "Drain: Willpower 6 + 2 Spell Pool = 8 dice vs TN 2 (Force 4 ÷ 2 − 1, at least 2): … → 3 successes". */
export function drainLine(f) {
    if (f.innate)
        return INNATE_DRAIN;
    const t = f.drain;
    const dice = t.spent ? `${t.label} ${t.rating} + ${t.spent.dice} ${t.spent.label} = ${t.dice} dice` : `${t.label} ${t.rating}`;
    const asked = t.half + t.modifier + t.extra + t.terms.reduce((n, x) => n + x.value, 0);
    const parts = `Force ${t.force} ÷ ${t.divide}${t.modifier ? ` ${signed(t.modifier)}` : ''}${t.extra ? ` ${signed(t.extra)} past the top` : ''}${t.terms.map((x) => ` ${signed(x.value)} ${x.label}`).join('')}${asked !== t.tn ? `, at least ${t.tn}` : ''}`;
    const res = disasterOf(f.rules, t) ? 'disaster' : plural(successesOf(f.rules, t));
    return `Drain: ${dice} vs TN ${t.tn} (${parts}): ${poolText(t.pool)} → ${res}`;
}
/** The drain's result: "Drain M staged down to L → 1 box Stun", "Drain S: no drain". */
export function drainResultLine(f) {
    if (f.innate)
        return 'No drain';
    const o = castOutcome(f);
    const from = o.drain.from.abbr;
    if (!o.drain.level)
        return `Drain ${from} staged below ${f.codes.levels[0].abbr}: no drain`;
    const moved = o.drain.steps ? `${from} staged down to ${o.drain.level.abbr}` : from;
    return `Drain ${moved} → ${o.drain.level.boxes} ${o.drain.level.boxes === 1 ? 'box' : 'boxes'} ${f.drain.kindLabel}`;
}
/** The sheet's own dice, each its own line (issue 1172): "Bear: +2 dice (health spells)". */
export const extraLines = (f) => (f.cast.bonus ?? []).map(diceTermLine);
/** The target's own dice on its resistance, each its own line (issue 1172): "Magic Resistance: +2 dice (spell resistance)". */
export const resistExtraLines = (f) => (f.resist?.bonus ?? []).map(diceTermLine);
/** An innate power's drain line (issue 1172). */
export const INNATE_DRAIN = 'Drain: none (an innate power; if it is sustained, the DM settles its drain)';
/** Every line of a cast, the DM's: the casting, the resistance, the effect, the drain and its result. */
export function castLines(f) {
    return [...extraLines(f), castLine(f), ...resistExtraLines(f), ...(f.resist ? [resistLine(f)] : []), effectLine(f), drainLine(f), ...(f.innate ? [] : [drainResultLine(f)])];
}
/**
 * A player's part of a cast: the caster's own players, on their own public cast, read the casting and the drain in full;
 * the target's own players read the resistance in full; everyone on a public cast reads each test's successes and the
 * effect, never a die, a rating or a TN — the target's Willpower and the caster's stay with the people they belong to.
 * A private cast shows no player anything but that it happened.
 */
export function playerCast(f, roll, sides, viewer) {
    if (roll.private)
        return { lines: [] };
    const o = castOutcome(f);
    const own = sides.attacker.owners.includes(roll.actor.id) && sides.attacker.owners.includes(viewer);
    const defending = sides.target.owners.includes(viewer) && sides.target.ownersSee !== false;
    const lines = [];
    if (own)
        lines.push(...extraLines(f));
    lines.push(own ? castLine(f) : `Cast ${f.spell.name}: ${o.disaster ? 'disaster' : plural(o.cast)}`);
    if (f.resist && defending)
        lines.push(...resistExtraLines(f));
    if (f.resist)
        lines.push(defending ? resistLine(f) : `Resist: ${plural(o.resist ?? 0)}`);
    lines.push(own || defending ? effectLine(f) : o.result === 'failed' ? 'The spell fails' : o.result === 'resisted' ? 'Resisted: no effect' : o.level ? `${o.level.label} ${f.damageKind.label}` : 'The spell takes effect');
    if (own)
        lines.push(drainLine(f), ...(f.innate ? [] : [drainResultLine(f)]));
    return { lines, name: f.spell.name, ...(f.spell.ref ? { ref: f.spell.ref } : {}) };
}
export function parsePlayerCast(v) {
    if (!isObj(v) || !Array.isArray(v.lines) || !v.lines.every((l) => typeof l === 'string'))
        return null;
    if ((v.name !== undefined && !isText(v.name, CAST_LIMITS.name)) || (v.ref !== undefined && (typeof v.ref !== 'string' || !ADDRESS_RE.test(v.ref))))
        return null;
    return { lines: [...v.lines], ...(typeof v.name === 'string' ? { name: v.name } : {}), ...(typeof v.ref === 'string' ? { ref: v.ref } : {}) };
}
// ── The form's helpers (the browser's preview; the server decides) ─────────
/**
 * A spell's drain code read off its entry (the spell family's numbers): the modifier, the level by the damage codes'
 * letters (L, M, S, D) or none when it is the level chosen at the cast, and the levels added. Null when the letters are
 * not the system's.
 */
export function drainOfEntry(e, codes) {
    const modifier = isInt(e.drain_modifier, -10, 10) ? e.drain_modifier : 0;
    const levelModifier = isInt(e.drain_level_modifier, -4, 4) ? e.drain_level_modifier : 0;
    if (e.drain_level === undefined || e.drain_level === null)
        return { modifier, levelModifier };
    const l = codes.levels.find((x) => x.abbr === e.drain_level);
    return l ? { modifier, level: l.key, levelModifier: 0 } : null;
}
//# sourceMappingURL=cast.js.map